import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslatedUI } from '../hooks/useTranslatedUI';
import { Button } from './ui/button';
import { Lock, Crown, ArrowRight, Shield, Phone, FileText, X, Star, Check } from 'lucide-react';

const PAYWALL_STRINGS = {
  unlockTitle: "Unlock Premium Access",
  premiumSchemes: "premium schemes",
  accessText: "Access",
  description: "Get expert assistance with eligibility checking, form filling, and application support.",
  eligibility: "Eligibility verification assistance",
  formFilling: "Form filling & document support",
  phoneConsult: "Phone consultation with experts",
  off: "75% OFF",
  yearPlan: "3 Year Plan",
  needHelp: "Need help?",
  subscribeNow: "Subscribe Now",
  maybeLater: "Maybe Later",
};

const Paywall = ({ onClose, premiumCount = 0, isAutoPopup = false }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const ui = useTranslatedUI(PAYWALL_STRINGS);

  // Handle ESC key to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Setup ESC key listener and disable body scroll
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow || 'unset';
    };
  }, [handleKeyDown]);

  const handleSubscribe = () => {
    onClose();
    navigate('/subscription');
  };

  const handleMaybeLater = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="paywall-container">
      {/* Dark semi-transparent backdrop */}
      <div 
        className="absolute inset-0 animate-fadeIn"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
        onClick={onClose}
        data-testid="paywall-backdrop"
      />
      
      {/* Modal wrapper - handles scrolling */}
      <div 
        className="absolute inset-0 overflow-y-auto flex items-start sm:items-center justify-center p-4 pointer-events-none"
      >
        {/* Modal content */}
        <div 
          className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl my-4 sm:my-8 animate-modalSlideIn pointer-events-auto"
          style={{ backgroundColor: '#ffffff' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="paywall-modal"
        >
          {/* Close Button - Fixed position within modal */}
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white z-50 text-gray-700 hover:text-gray-900 shadow-lg border border-gray-200/50 transition-all duration-200 hover:scale-110"
            data-testid="paywall-close-btn"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header with gradient background */}
          <div className="bg-gradient-to-br from-green-600 via-green-500 to-emerald-600 p-5 sm:p-6 text-center relative overflow-hidden rounded-t-2xl">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 sm:w-18 sm:h-18 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-white/30">
                <Crown className="w-8 h-8 sm:w-9 sm:h-9 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white" data-testid="paywall-title">
                {ui.unlockTitle}
              </h2>
              <p className="text-white/90 text-sm sm:text-base">
                {ui.accessText} <span className="font-bold text-yellow-200">{premiumCount > 0 ? `${premiumCount}+` : '500+'}</span> {ui.premiumSchemes}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 sm:p-6 bg-white" style={{ backgroundColor: '#ffffff' }}>
            <p className="text-gray-700 text-center mb-4 text-sm">
              {ui.description}
            </p>

            {/* Features - Compact */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-3 p-2.5 bg-green-50 rounded-lg border border-green-100">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-gray-800 text-sm">{ui.eligibility}</span>
              </div>
              <div className="flex items-center gap-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-gray-800 text-sm">{ui.formFilling}</span>
              </div>
              <div className="flex items-center gap-3 p-2.5 bg-purple-50 rounded-lg border border-purple-100">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-gray-800 text-sm">{ui.phoneConsult}</span>
              </div>
            </div>

            {/* Pricing Card */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-4 mb-4 text-white shadow-lg relative overflow-hidden">
              <div className="absolute top-2 right-2">
                <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                  {ui.off}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-green-100 text-xs mb-0.5">{ui.yearPlan}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">₹1,499</span>
                    <span className="text-green-200 text-sm line-through">₹5,999</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Crown className="w-6 h-6 text-yellow-300" />
                </div>
              </div>
            </div>

            {/* Contact for Assistance */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-green-800 text-center">
                <strong>{ui.needHelp}</strong> WhatsApp: <a href="https://wa.me/919187636397" className="font-bold text-green-700 hover:underline">+91 91876 36397</a>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleSubscribe}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 font-semibold"
                data-testid="paywall-subscribe-btn"
              >
                {ui.subscribeNow}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={handleMaybeLater}
                variant="outline"
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 py-2.5"
                data-testid="paywall-later-btn"
              >
                {ui.maybeLater}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-modalSlideIn {
          animation: modalSlideIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Paywall;
